import React, { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';

/**
 * Accessible looping video: muted, no autoplay when the user prefers reduced
 * motion (poster frame is shown instead), always pausable via toggle button.
 *
 * Wird von ResponsiveImageSet für Breakpoints mit `mediaType: 'video'` genutzt.
 */
const VideoPlayer = ({ src, poster, style, altText, uniqueId, onNaturalAspectRatio }) => {
    const videoRef = useRef(null);
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [playing, setPlaying] = useState(false);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        if (prefersReducedMotion) {
            video.pause();
        } else {
            video.play().catch(() => {});
        }
    }, [prefersReducedMotion, src]);

    const togglePlay = () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
            video.play().catch(() => {});
        } else {
            video.pause();
        }
    };

    const handleLoadedMetadata = (e) => {
        const video = e.target;
        if (video.videoWidth && video.videoHeight) {
            // Mirrors the img onLoad logic so scale can compute the aspect ratio
            onNaturalAspectRatio?.(video.videoWidth / video.videoHeight);
        }
    };

    // Without a poster, the media fragment seeks to the start so the first
    // video frame renders reliably (e.g. paused under reduced motion)
    const effectiveSrc = poster ? src : `${src}#t=0.001`;

    return (
        <>
            <video
                ref={(el) => {
                    videoRef.current = el;
                    // React applies `muted` too late for autoplay policies — set it eagerly
                    if (el) el.muted = true;
                }}
                // d-block removes the inline baseline whitespace below the video so the
                // pause button's bottom margin aligns with the visible video edge
                className={`w-100 img-fluid d-block ${uniqueId}`}
                style={style}
                src={effectiveSrc}
                poster={poster || undefined}
                muted
                loop
                playsInline
                autoPlay={!prefersReducedMotion}
                preload="metadata"
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                aria-label={altText || undefined}
            />
            <button
                type="button"
                className={`btn btn-sm position-absolute bottom-0 end-0 m-2 ${playing ? 'btn-light' : 'btn-primary'}`}
                onClick={togglePlay}
                aria-label={playing ? 'Video pausieren' : 'Video abspielen'}
            >
                {playing ? (
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path d="M5 3.5h2v9H5zM9 3.5h2v9H9z" />
                    </svg>
                ) : (
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path d="M4 3l9 5-9 5z" />
                    </svg>
                )}
            </button>
        </>
    );
};

export default VideoPlayer;
